import { useState } from 'react';
import { ImageIcon, PencilIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { z } from 'zod';
import { adminBannerCreateSchema, type AdminBanner, type AdminBannerUpdate } from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Field } from '@/components/field';
import { FormDialog } from '@/components/form-dialog';
import { ImageUploadField } from '@/components/image-upload-field';
import { PageHeader } from '@/components/page-header';
import { optionalNumberField, trimmed, useAdminForm } from '@/lib/admin-form';
import { EmptyState, ErrorNotice, TableSkeleton } from '@/components/states';
import {
  useAdminBanners,
  useCreateBanner,
  useDeleteBanner,
  useUpdateBanner,
} from '@/lib/admin-queries';
import { strings } from '@/i18n/strings';

const BANNER_COLUMNS = 6;

/**
 * Tashqi havola Mini App'da `target="_blank"` bilan ochiladi, ya'ni `href` ga AYNAN shu qiymat
 * tushadi. Shuning uchun sxema shu yerda tekshiriladi: `javascript:` yoki yarim yozilgan manzil
 * front'da jimgina bosilmaydigan bannerga aylanib qolgandan ko'ra, saqlashdan OLDIN aytilgani
 * yaxshi.
 */
function isValidExternalUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Yaratish/tahrirlash formasining sxemasi.
 *
 * Havola qoidasi shartnomadan olinadi, forma esa bo'sh pozitsiyani "berilmagan" deb o'qiydi va
 * `<select>` larning satrini mantiqiy qiymatga aylantiradi. Rasm bu yerda YO'Q: u formadagi
 * maydon emas, alohida yuklash oqimining natijasi, va u kelmaguncha tugma o'chirilgan turadi.
 *
 * Sxema turi (`external`) HAVOLANING o'zini tekshiradi, shuning uchun qoida `superRefine` da —
 * ikki maydon birgalikda ma'noga ega: "rewards" ilova ichida to'g'ri, tashqi havola sifatida esa
 * bosilmaydigan banner.
 */
const bannerFormSchema = z
  .object({
    link: trimmed(adminBannerCreateSchema.shape.link),
    external: z.enum(['internal', 'external']),
    position: optionalNumberField,
    active: z.enum(['active', 'inactive']),
  })
  .superRefine((values, ctx) => {
    if (values.external === 'external' && !isValidExternalUrl(values.link)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['link'],
        message: strings.banners.linkExternalInvalid,
      });
    }
  });

const EMPTY_BANNER_FORM = {
  link: '',
  external: 'internal',
  position: '',
  active: 'active',
} as const;

/** Mavjud bannerning qiymatlari — tahrirlash formasi ular bilan boshlanadi. */
function rowValues(banner: AdminBanner) {
  return {
    link: banner.link,
    external: banner.external ? ('external' as const) : ('internal' as const),
    position: String(banner.position),
    active: banner.active ? ('active' as const) : ('inactive' as const),
  };
}

/**
 * Bosh ekrandagi banner-slayder boshqaruvi.
 *
 * Struktura ball-katalog sahifasi bilan bir xil (ro'yxat + yaratish formasi + qatorda tahrirlash
 * / o'chirish) — faqat rasm maydoni QO'SHILGAN va MAJBURIY: banner rasmsiz Mini App'da hech
 * narsa ko'rsatmaydi, shuning uchun kalit olinmagunicha "Qo'shish" bosilmaydi.
 */
export function BannersPage() {
  const banners = useAdminBanners();
  const createBanner = useCreateBanner();
  const updateBanner = useUpdateBanner();
  const deleteBanner = useDeleteBanner();

  const [createOpen, setCreateOpen] = useState(false);
  const [imageKey, setImageKey] = useState<string | null>(null);
  // Muvaffaqiyatli yaratishdan keyin rasm maydonini qayta tug'dirib, uning ichki holatini
  // (oldindan ko'rish, yuklash holati) ham tozalaydi.
  const [formResetCount, setFormResetCount] = useState(0);

  const form = useAdminForm({ schema: bannerFormSchema, defaultValues: EMPTY_BANNER_FORM });

  const rows = banners.data?.items ?? [];

  const resetForm = () => {
    form.reset(EMPTY_BANNER_FORM);
    setImageKey(null);
    setFormResetCount((count) => count + 1);
  };

  const submitCreate = form.handleSubmit((values) => {
    /*
      Rasm sxemada emas, chunki u formadagi maydon emas — u alohida yuklash oqimidan kelgan
      kalit. Tugma baribir kalitsiz o'chirilgan (`submitDisabled`), ya'ni bu tekshiruv
      yetib bo'lmaydigan holat uchun himoya, xabar uchun emas.
    */
    if (imageKey === null) return;

    createBanner.mutate(
      {
        imageKey,
        link: values.link,
        external: values.external === 'external',
        ...(values.position === undefined ? {} : { position: values.position }),
        active: values.active === 'active',
      },
      {
        // Muvaffaqiyatdan keyin oyna yopiladi va forma tozalanadi: aks holda operator uni
        // ikkinchi marta yuborib, bitta bannerni ikki marta yaratib qo'yishi mumkin edi.
        onSuccess: () => {
          setCreateOpen(false);
          resetForm();
        },
        onError: form.applyServerError,
      },
    );
  });

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title={strings.banners.title}
        description={strings.banners.subtitle}
        actions={
          <FormDialog
            open={createOpen}
            onOpenChange={(open) => {
              setCreateOpen(open);
              // Yopilganda forma tozalanadi — yarim yuklangan rasm keyingi ochilishda
              // "kimdir allaqachon boshlagan" degan taassurot qoldirardi.
              if (!open) resetForm();
            }}
            trigger={
              <Button>
                <PlusIcon aria-hidden="true" />
                {strings.banners.createHeading}
              </Button>
            }
            title={strings.banners.createHeading}
            submitLabel={createBanner.isPending ? strings.banners.creating : strings.banners.create}
            submitting={createBanner.isPending}
            // Rasm banner uchun MAJBURIY: rasmsiz banner Mini App'da hech narsa ko'rsatmaydi.
            submitDisabled={imageKey === null}
            onSubmit={submitCreate}
            error={form.serverError ? <ErrorNotice>{form.serverError}</ErrorNotice> : null}
          >
            <ImageUploadField
              key={formResetCount}
              kind="banner"
              label={strings.banners.image}
              onUploaded={setImageKey}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={strings.banners.linkKind}>
                <Select {...form.register('external')}>
                  <option value="internal">{strings.banners.linkKindInternal}</option>
                  <option value="external">{strings.banners.linkKindExternal}</option>
                </Select>
              </Field>

              <Field
                label={strings.banners.link}
                hint={strings.banners.linkHint}
                error={form.formState.errors.link?.message}
              >
                <Input {...form.register('link')} />
              </Field>

              <Field
                label={strings.banners.position}
                hint={strings.banners.positionHint}
                error={form.formState.errors.position?.message}
              >
                <Input type="number" {...form.register('position')} />
              </Field>

              <Field label={strings.banners.state}>
                <Select {...form.register('active')}>
                  <option value="active">{strings.banners.active}</option>
                  <option value="inactive">{strings.banners.inactive}</option>
                </Select>
              </Field>
            </div>
          </FormDialog>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{strings.banners.listHeading}</CardTitle>
          <CardDescription>{strings.banners.hint}</CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {banners.isError ? <ErrorNotice error={banners.error} /> : null}
          {updateBanner.isError ? <ErrorNotice error={updateBanner.error} /> : null}
          {deleteBanner.isError ? <ErrorNotice error={deleteBanner.error} /> : null}

          {banners.isSuccess && rows.length === 0 ? (
            <EmptyState title={strings.banners.empty} icon={ImageIcon} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{strings.banners.columnImage}</TableHead>
                  <TableHead>{strings.banners.columnLink}</TableHead>
                  <TableHead>{strings.banners.columnLinkKind}</TableHead>
                  <TableHead className="text-right">{strings.banners.columnPosition}</TableHead>
                  <TableHead>{strings.banners.columnState}</TableHead>
                  <TableHead>{strings.banners.columnActions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {banners.isPending ? <TableSkeleton columns={BANNER_COLUMNS} /> : null}
                {rows.map((banner) => (
                  <BannerRow
                    key={banner.id}
                    banner={banner}
                    onSave={(patch) => updateBanner.mutate({ id: banner.id, ...patch })}
                    onDelete={() => deleteBanner.mutate(banner.id)}
                    saving={updateBanner.isPending}
                    deleting={deleteBanner.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

interface BannerRowProps {
  banner: AdminBanner;
  onSave: (patch: AdminBannerUpdate) => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
}

function BannerRow({ banner, onSave, onDelete, saving, deleting }: BannerRowProps) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [imageKey, setImageKey] = useState(banner.imageKey);

  // Qatordagi tahrirlash yaratish formasi bilan BIR XIL sxemani ishlatadi — ikkalasi bir xil
  // bannerni tavsiflaydi, va ikkita tekshiruv yo'li vaqt o'tishi bilan ajralib ketardi.
  const form = useAdminForm({ schema: bannerFormSchema, defaultValues: rowValues(banner) });

  const startEdit = () => {
    form.reset(rowValues(banner));
    setImageKey(banner.imageKey);
    setEditing(true);
  };

  const save = form.handleSubmit((values) => {
    onSave({
      imageKey,
      link: values.link,
      external: values.external === 'external',
      position: values.position ?? banner.position,
      active: values.active === 'active',
    });
    setEditing(false);
  });

  if (editing) {
    return (
      <TableRow>
        <TableCell colSpan={BANNER_COLUMNS}>
          <div className="flex flex-col gap-4 py-2">
            <ImageUploadField
              kind="banner"
              label={`${strings.banners.image} — ${banner.link}`}
              imageUrl={banner.imageUrl}
              onUploaded={setImageKey}
              inputAriaLabel={`${strings.banners.image} — ${banner.link}`}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={strings.banners.linkKind}>
                <Select
                  aria-label={`${strings.banners.linkKind} — ${banner.link}`}
                  {...form.register('external')}
                >
                  <option value="internal">{strings.banners.linkKindInternal}</option>
                  <option value="external">{strings.banners.linkKindExternal}</option>
                </Select>
              </Field>

              <Field
                label={strings.banners.link}
                hint={strings.banners.linkHint}
                error={form.formState.errors.link?.message}
              >
                <Input
                  aria-label={`${strings.banners.link} — ${banner.link}`}
                  {...form.register('link')}
                />
              </Field>

              <Field
                label={strings.banners.position}
                error={form.formState.errors.position?.message}
              >
                <Input
                  type="number"
                  aria-label={`${strings.banners.position} — ${banner.link}`}
                  {...form.register('position')}
                />
              </Field>

              <Field label={strings.banners.state}>
                <Select
                  aria-label={`${strings.banners.state} — ${banner.link}`}
                  {...form.register('active')}
                >
                  <option value="active">{strings.banners.active}</option>
                  <option value="inactive">{strings.banners.inactive}</option>
                </Select>
              </Field>
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" onClick={save} loading={saving}>
                {saving ? strings.banners.saving : strings.banners.save}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                {strings.banners.cancel}
              </Button>
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        {banner.imageUrl ? (
          <img
            src={banner.imageUrl}
            alt={strings.uploads.thumbnailAlt}
            className="border-input size-10 rounded-md border object-cover"
          />
        ) : (
          <div className="border-input bg-muted text-muted-foreground flex size-10 items-center justify-center rounded-md border text-[9px]">
            {strings.uploads.noImage}
          </div>
        )}
      </TableCell>
      <TableCell className="font-medium break-all">{banner.link}</TableCell>
      <TableCell>
        <Badge variant="outline">
          {banner.external ? strings.banners.linkKindExternal : strings.banners.linkKindInternal}
        </Badge>
      </TableCell>
      <TableCell className="tabular text-right">{banner.position}</TableCell>
      <TableCell>
        <Badge variant={banner.active ? 'success' : 'outline'}>
          {banner.active ? strings.banners.active : strings.banners.inactive}
        </Badge>
      </TableCell>
      <TableCell>
        {/*
          O'chirish ikki bosqichda: birinchi bosish faqat tasdiqni ko'rsatadi, DELETE so'rovi
          esa faqat ikkinchi bosishda ketadi.
        */}
        {confirmingDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">{strings.banners.deleteConfirm}</span>
            <Button size="sm" variant="destructive" onClick={onDelete} loading={deleting}>
              {deleting ? strings.banners.deleting : strings.banners.deleteConfirmYes}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
            >
              {strings.banners.cancel}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={startEdit}>
              <PencilIcon aria-hidden="true" />
              {strings.banners.edit}
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setConfirmingDelete(true)}>
              <TrashIcon aria-hidden="true" />
              {strings.banners.delete}
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
